import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { TreePine, Rocket, DollarSign, Target, BarChart3, MousePointerClick, Users, Eye, ShoppingCart, TrendingUp } from 'lucide-react';
export function AdsSlide() {
  const [view, setView] = useState<'90day' | '12mo'>('90day');
  // === ORGANIC (Grow Plan) ===
  // $447/mo flat. No ad spend. SEO compounds.
  // Revenue ramp: near-zero months 1-7, kicks in month 8+
  // $0 cost per organic acquisition once rankings hit
  const organicMonthlyCost = 447;
  const organicMonthlyRevenue = [0, 0, 0, 200, 400, 800, 1500, 3000, 5000, 7000, 9000, 11000];
  // === PAID (Ad-heavy approach) ===
  // $747/mo management + ~$1,500/mo ad spend = $2,247/mo
  // Immediate results but you pay per click forever
  // Revenue is consistent but cost never drops
  const paidMonthlyCost = 2247; // $747 mgmt + $1,500 ad spend
  const paidMonthlyRevenue = [2500, 3500, 4500, 4500, 4500, 4500, 4500, 4500, 4500, 4500, 4500, 4500];
  // 90-day calculations
  const organic90Cost = organicMonthlyCost * 3;
  const organic90Rev = organicMonthlyRevenue.slice(0, 3).reduce((a, b) => a + b, 0);
  const organic90ROI = organic90Rev > 0 ? (organic90Rev - organic90Cost) / organic90Cost * 100 : -100;
  const paid90Cost = paidMonthlyCost * 3;
  const paid90Rev = paidMonthlyRevenue.slice(0, 3).reduce((a, b) => a + b, 0);
  const paid90ROI = (paid90Rev - paid90Cost) / paid90Cost * 100;
  // 12-month calculations
  const organic12Cost = organicMonthlyCost * 12;
  const organic12Rev = organicMonthlyRevenue.reduce((a, b) => a + b, 0);
  const organic12ROI = (organic12Rev - organic12Cost) / organic12Cost * 100;
  const paid12Cost = paidMonthlyCost * 12;
  const paid12Rev = paidMonthlyRevenue.reduce((a, b) => a + b, 0);
  const paid12ROI = (paid12Rev - paid12Cost) / paid12Cost * 100;
  // 12-month profit
  const organic12Profit = organic12Rev - organic12Cost;
  const paid12Profit = paid12Rev - paid12Cost;
  // Chart data: max height reference
  const allRevValues = [...organicMonthlyRevenue, ...paidMonthlyRevenue];
  const maxRev = Math.max(...allRevValues, 1);
  return <section id="ads" className="min-h-screen bg-dark py-24 px-4 flex flex-col justify-center" data-id="element-29">
      <div className="max-w-7xl mx-auto w-full" data-id="element-30">
        <div className="text-center mb-16" data-id="element-31">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-3xl md:text-5xl font-bold mb-4" data-id="element-32">
            Paid vs Organic Traffic
          </motion.h2>
          <motion.p initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.2
        }} className="text-xl text-text-muted" data-id="element-33">
            Most agencies pick one. We build both.
          </motion.p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16" data-id="element-34">
          {/* Organic Card */}
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} className="bg-dark-card border border-green-500/30 rounded-2xl p-8 relative overflow-hidden group hover:bg-green-900/5 transition-colors" data-id="element-35">
            <div className="flex items-center mb-6" data-id="element-36">
              <div className="p-3 bg-green-500/20 rounded-lg mr-4" data-id="element-37">
                <TreePine className="text-green-500" size={32} data-id="element-38" />
              </div>
              <div data-id="element-39">
                <h3 className="text-2xl font-bold text-white" data-id="element-40">
                  Organic Growth
                </h3>
                <span className="text-green-400 text-sm font-medium" data-id="element-41">
                  Grow Plan — $447/mo
                </span>
              </div>
            </div>
            <ul className="space-y-4 text-text-muted" data-id="element-42">
              <li className="flex items-center" data-id="element-43">
                <span className="w-2 h-2 bg-green-500 rounded-full mr-3" data-id="element-44"></span>
                Built once, compounds over time
              </li>
              <li className="flex items-center" data-id="element-45">
                <span className="w-2 h-2 bg-green-500 rounded-full mr-3" data-id="element-46"></span>
                $0 cost per click after rankings hit
              </li>
              <li className="flex items-center" data-id="element-47">
                <span className="w-2 h-2 bg-green-500 rounded-full mr-3" data-id="element-48"></span>
                Slow start — real results by month 8
              </li>
              <li className="flex items-center" data-id="element-49">
                <span className="w-2 h-2 bg-green-500 rounded-full mr-3" data-id="element-50"></span>
                Traffic grows even if you pause spending
              </li>
            </ul>
          </motion.div>

          {/* Paid Card */}
          <motion.div initial={{
          opacity: 0,
          x: 30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} className="bg-dark-card border border-orange-500/30 rounded-2xl p-8 relative overflow-hidden group hover:bg-orange-900/5 transition-colors" data-id="element-51">
            <div className="flex items-center mb-6" data-id="element-52">
              <div className="p-3 bg-orange-500/20 rounded-lg mr-4" data-id="element-53">
                <Rocket className="text-orange-500" size={32} data-id="element-54" />
              </div>
              <div data-id="element-55">
                <h3 className="text-2xl font-bold text-white" data-id="element-56">
                  Paid Acceleration
                </h3>
                <span className="text-orange-400 text-sm font-medium" data-id="element-57">
                  ~$2,247/mo (mgmt + ad spend)
                </span>
              </div>
            </div>
            <ul className="space-y-4 text-text-muted" data-id="element-58">
              <li className="flex items-center" data-id="element-59">
                <span className="w-2 h-2 bg-orange-500 rounded-full mr-3" data-id="element-60"></span>
                Immediate visibility from day 1
              </li>
              <li className="flex items-center" data-id="element-61">
                <span className="w-2 h-2 bg-orange-500 rounded-full mr-3" data-id="element-62"></span>
                Pay per impression — costs never drop
              </li>
              <li className="flex items-center" data-id="element-63">
                <span className="w-2 h-2 bg-orange-500 rounded-full mr-3" data-id="element-64"></span>
                $3-15 per click, every click
              </li>
              <li className="flex items-center" data-id="element-65">
                <span className="w-2 h-2 bg-orange-500 rounded-full mr-3" data-id="element-66"></span>
                Traffic stops the moment you stop paying
              </li>
            </ul>
          </motion.div>
        </div>

        {/* Budget Allocation Simulator */}
        <div className="bg-dark-card rounded-2xl p-8 border border-white/10 mb-16" data-id="element-67">
          <h3 className="text-2xl font-bold mb-2 text-center" data-id="element-68">
            Budget Allocation Simulator
          </h3>
          <p className="text-text-muted text-center text-sm mb-8" data-id="element-69">
            Compare the real cost and ROI of organic vs paid — short-term and
            long-term
          </p>

          {/* Toggle: 90 days vs 12 months */}
          <div className="flex justify-center mb-8" data-id="element-70">
            <div className="bg-dark rounded-full p-1 flex gap-1 border border-white/10" data-id="element-71">
              <button onClick={() => setView('90day')} className={`px-5 py-2 rounded-full text-sm font-bold transition-all ${view === '90day' ? 'bg-primary text-white shadow-lg shadow-primary/20' : 'text-text-muted hover:text-white'}`} data-id="element-72">
                First 90 Days
              </button>
              <button onClick={() => setView('12mo')} className={`px-5 py-2 rounded-full text-sm font-bold transition-all ${view === '12mo' ? 'bg-primary text-white shadow-lg shadow-primary/20' : 'text-text-muted hover:text-white'}`} data-id="element-73">
                12 Months
              </button>
            </div>
          </div>

          {/* Comparison Cards */}
          <motion.div key={view} initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.3
        }} className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10" data-id="element-74">
            {/* Organic Column */}
            <div className="bg-green-500/5 border border-green-500/20 rounded-xl p-6" data-id="element-75">
              <div className="flex items-center gap-2 mb-4" data-id="element-76">
                <TreePine size={18} className="text-green-400" data-id="element-77" />
                <h4 className="font-bold text-green-400" data-id="element-78">
                  Organic (Grow Plan)
                </h4>
              </div>
              <div className="space-y-4" data-id="element-79">
                <div className="flex justify-between items-center" data-id="element-80">
                  <span className="text-sm text-text-muted" data-id="element-81">Total Cost</span>
                  <span className="text-xl font-bold text-white" data-id="element-82">
                    $
                    {(view === '90day' ? organic90Cost : organic12Cost).toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between items-center" data-id="element-83">
                  <span className="text-sm text-text-muted" data-id="element-84">
                    Revenue Generated
                  </span>
                  <span className="text-xl font-bold text-white" data-id="element-85">
                    $
                    {(view === '90day' ? organic90Rev : organic12Rev).toLocaleString()}
                  </span>
                </div>
                <div className="h-px bg-white/10" data-id="element-86" />
                <div className="flex justify-between items-center" data-id="element-87">
                  <span className="text-sm text-text-muted" data-id="element-88">ROI</span>
                  <span className={`text-2xl font-bold ${(view === '90day' ? organic90ROI : organic12ROI) >= 0 ? 'text-green-400' : 'text-red-400'}`} data-id="element-89">
                    {(view === '90day' ? organic90ROI : organic12ROI).toFixed(0)}
                    %
                  </span>
                </div>
                {view === '12mo' && <div className="flex justify-between items-center" data-id="element-90">
                    <span className="text-sm text-text-muted" data-id="element-91">Net Profit</span>
                    <span className="text-xl font-bold text-green-400" data-id="element-92">
                      ${organic12Profit.toLocaleString()}
                    </span>
                  </div>}
              </div>
              {view === '90day' && <p className="text-xs text-green-400/60 mt-4 italic" data-id="element-93">
                  SEO is building your foundation — rankings haven't kicked in
                  yet. Patience pays off.
                </p>}
              {view === '12mo' && <p className="text-xs text-green-400/60 mt-4 italic" data-id="element-94">
                  Month 8+ is where organic explodes. $0 per click means every
                  new visitor is pure profit.
                </p>}
            </div>

            {/* Paid Column */}
            <div className="bg-orange-500/5 border border-orange-500/20 rounded-xl p-6" data-id="element-95">
              <div className="flex items-center gap-2 mb-4" data-id="element-96">
                <Rocket size={18} className="text-orange-400" data-id="element-97" />
                <h4 className="font-bold text-orange-400" data-id="element-98">Paid Ads</h4>
              </div>
              <div className="space-y-4" data-id="element-99">
                <div className="flex justify-between items-center" data-id="element-100">
                  <span className="text-sm text-text-muted" data-id="element-101">Total Cost</span>
                  <span className="text-xl font-bold text-white" data-id="element-102">
                    $
                    {(view === '90day' ? paid90Cost : paid12Cost).toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between items-center" data-id="element-103">
                  <span className="text-sm text-text-muted" data-id="element-104">
                    Revenue Generated
                  </span>
                  <span className="text-xl font-bold text-white" data-id="element-105">
                    $
                    {(view === '90day' ? paid90Rev : paid12Rev).toLocaleString()}
                  </span>
                </div>
                <div className="h-px bg-white/10" data-id="element-106" />
                <div className="flex justify-between items-center" data-id="element-107">
                  <span className="text-sm text-text-muted" data-id="element-108">ROI</span>
                  <span className={`text-2xl font-bold ${(view === '90day' ? paid90ROI : paid12ROI) >= 0 ? 'text-green-400' : 'text-red-400'}`} data-id="element-109">
                    {(view === '90day' ? paid90ROI : paid12ROI).toFixed(0)}%
                  </span>
                </div>
                {view === '12mo' && <div className="flex justify-between items-center" data-id="element-110">
                    <span className="text-sm text-text-muted" data-id="element-111">Net Profit</span>
                    <span className="text-xl font-bold text-orange-400" data-id="element-112">
                      ${paid12Profit.toLocaleString()}
                    </span>
                  </div>}
              </div>
              {view === '90day' && <p className="text-xs text-orange-400/60 mt-4 italic" data-id="element-113">
                  Ads deliver leads from week 1. But you're paying for every
                  single click.
                </p>}
              {view === '12mo' && <p className="text-xs text-orange-400/60 mt-4 italic" data-id="element-114">
                  Consistent revenue, but costs never drop. You pay per
                  impression forever.
                </p>}
            </div>
          </motion.div>

          {/* The punchline */}
          <motion.div key={view + '-insight'} initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 0.2
        }} className={`p-4 rounded-xl text-center mb-10 ${view === '90day' ? 'bg-orange-500/10 border border-orange-500/20' : 'bg-green-500/10 border border-green-500/20'}`} data-id="element-115">
            {view === '90day' ? <p className="text-sm font-medium" data-id="element-116">
                <span className="text-orange-400 font-bold" data-id="element-117">
                  At 90 days, paid wins on speed.
                </span>{' '}
                <span className="text-text-muted" data-id="element-118">
                  But you've spent {Math.round(paid90Cost / organic90Cost)}x
                  more. Organic is still building — the real payoff is coming.
                </span>
              </p> : <p className="text-sm font-medium" data-id="element-119">
                <span className="text-green-400 font-bold" data-id="element-120">
                  At 12 months, organic delivers {Math.round(organic12ROI)}% ROI
                  vs {Math.round(paid12ROI)}% for paid.
                </span>{' '}
                <span className="text-text-muted" data-id="element-121">
                  That's ${organic12Profit.toLocaleString()} profit from organic
                  vs ${paid12Profit.toLocaleString()} from paid — and organic
                  keeps growing.
                </span>
              </p>}
          </motion.div>

          {/* Chart Visualization */}
          <div className="mb-2" data-id="element-122">
            <div className="flex items-center justify-between mb-4" data-id="element-123">
              <span className="text-xs text-text-dim uppercase tracking-wider" data-id="element-124">
                Monthly Revenue
              </span>
              <div className="flex items-center gap-4 text-xs" data-id="element-125">
                <span className="flex items-center gap-1.5" data-id="element-126">
                  <span className="w-3 h-3 rounded-sm bg-green-500/80" data-id="element-127"></span>
                  Organic
                </span>
                <span className="flex items-center gap-1.5" data-id="element-128">
                  <span className="w-3 h-3 rounded-sm bg-orange-500/80" data-id="element-129"></span>
                  Paid
                </span>
              </div>
            </div>
            <div className="h-48 flex items-end gap-1 px-1" data-id="element-130">
              {Array.from({
              length: 12
            }).map((_, i) => {
              const oHeight = organicMonthlyRevenue[i] / maxRev * 100;
              const pHeight = paidMonthlyRevenue[i] / maxRev * 100;
              const isHighlighted = view === '90day' ? i < 3 : true;
              return <div key={i} className={`w-full flex gap-[2px] items-end h-full group relative transition-opacity duration-300 ${isHighlighted ? 'opacity-100' : 'opacity-30'}`} data-id="element-131">
                    {/* Organic bar */}
                    <div className="w-1/2 bg-green-500/80 rounded-t-sm transition-all duration-500" style={{
                  height: `${Math.max(oHeight, 1)}%`
                }} data-id="element-132" />
                    {/* Paid bar */}
                    <div className="w-1/2 bg-orange-500/80 rounded-t-sm transition-all duration-500" style={{
                  height: `${Math.max(pHeight, 1)}%`
                }} data-id="element-133" />
                    {/* Tooltip */}
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 bg-black/90 text-[10px] p-2 rounded opacity-0 group-hover:opacity-100 whitespace-nowrap z-10 pointer-events-none border border-white/10" data-id="element-134">
                      <p className="font-bold text-white mb-1" data-id="element-135">Month {i + 1}</p>
                      <p className="text-green-400" data-id="element-136">
                        Organic: ${organicMonthlyRevenue[i].toLocaleString()}
                      </p>
                      <p className="text-orange-400" data-id="element-137">
                        Paid: ${paidMonthlyRevenue[i].toLocaleString()}
                      </p>
                    </div>
                  </div>;
            })}
            </div>
            <div className="flex justify-between text-[10px] text-text-dim mt-2 px-1" data-id="element-138">
              {Array.from({
              length: 12
            }).map((_, i) => <span key={i} className="w-full text-center" data-id="element-139">
                  {i + 1}
                </span>)}
            </div>
            <p className="text-center text-xs text-text-dim mt-1" data-id="element-140">Month</p>
          </div>

          {/* Month 8 callout */}
          <div className="mt-6 flex items-center justify-center gap-2 text-sm" data-id="element-141">
            <TrendingUp size={16} className="text-green-400" data-id="element-142" />
            <span className="text-text-muted" data-id="element-143">
              Month 8: Organic revenue{' '}
              <span className="text-green-400 font-bold" data-id="element-144">surpasses</span> paid —
              and it's all profit from there.
            </span>
          </div>
        </div>

        <div className="mt-12 text-center max-w-3xl mx-auto mb-24" data-id="element-145">
          <p className="text-lg italic text-text-muted border-l-4 border-primary pl-6 text-left" data-id="element-146">
            "Your organic foundation grows for free while paid campaigns
            accelerate results immediately.
            <span className="text-white font-bold not-italic" data-id="element-147">
              {' '}
              Paid traffic funds organic growth.
            </span>
            "
          </p>
        </div>

        {/* Retargeting Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-24" data-id="element-148">
          <div className="flex flex-col justify-center" data-id="element-149">
            <h3 className="text-3xl font-bold mb-4" data-id="element-150">
              Stop Wasting Money on Cold Traffic
            </h3>
            <p className="text-text-muted text-lg mb-8" data-id="element-151">
              The Retargeting Advantage: Spend pennies instead of dollars to
              ONLY retarget high-intent traffic.
            </p>
            <div className="bg-primary/10 border border-primary/30 p-6 rounded-xl" data-id="element-152">
              <div className="flex items-center mb-4" data-id="element-153">
                <Target className="text-primary mr-3" size={32} data-id="element-154" />
                <span className="text-2xl font-bold text-white" data-id="element-155">10x+ ROAS</span>
              </div>
              <p className="text-sm text-text-dim" data-id="element-156">
                With retargeting campaigns vs cold traffic
              </p>
            </div>
          </div>

          <div className="bg-dark-card border border-white/10 rounded-2xl p-8 flex flex-col justify-center" data-id="element-157">
            <div className="flex items-center justify-between mb-6 pb-6 border-b border-white/5" data-id="element-158">
              <div className="text-left" data-id="element-159">
                <p className="text-sm text-text-muted mb-1" data-id="element-160">Cold Traffic CPC</p>
                <p className="text-3xl font-bold text-red-400" data-id="element-161">$5.00</p>
              </div>
              <div className="text-right" data-id="element-162">
                <p className="text-sm text-text-muted mb-1" data-id="element-163">Retargeting CPC</p>
                <p className="text-3xl font-bold text-green-400" data-id="element-164">$0.50</p>
              </div>
            </div>
            <div className="w-full bg-white/5 rounded-full h-4 overflow-hidden" data-id="element-165">
              <div className="h-full bg-red-500 w-full relative" data-id="element-166">
                <div className="absolute right-0 top-0 h-full bg-green-500 w-[10%]" data-id="element-167"></div>
              </div>
            </div>
            <p className="text-center text-xs text-text-dim mt-2" data-id="element-168">
              Cost comparison visual
            </p>
          </div>
        </div>

        {/* Attribution Section */}
        <div className="bg-gradient-to-br from-dark-card to-blue-900/10 rounded-3xl p-8 md:p-12 border border-white/10" data-id="element-169">
          <div className="text-center mb-12" data-id="element-170">
            <h3 className="text-3xl font-bold mb-4" data-id="element-171">
              Attribution: Know Where Every Customer Comes From
            </h3>
            <p className="text-text-muted max-w-2xl mx-auto" data-id="element-172">
              Most businesses don't track properly. You need to track everything
              and send that data back to your pixel to optimize for conversions.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12" data-id="element-173">
            {[{
            icon: MousePointerClick,
            label: 'CPC',
            desc: 'Cost Per Click'
          }, {
            icon: Target,
            label: 'CTR',
            desc: 'Click Through Rate'
          }, {
            icon: Users,
            label: 'CAC',
            desc: 'Customer Acq. Cost'
          }, {
            icon: DollarSign,
            label: 'LTV',
            desc: 'Lifetime Value'
          }, {
            icon: Eye,
            label: 'Pageviews',
            desc: 'Traffic Volume'
          }, {
            icon: Users,
            label: 'Leads',
            desc: 'Qualified Prospects'
          }, {
            icon: ShoppingCart,
            label: 'Conversions',
            desc: 'Sales/Bookings'
          }, {
            icon: BarChart3,
            label: 'ROAS',
            desc: 'Return on Ad Spend'
          }].map((metric, i) => <div key={i} className="bg-black/20 p-4 rounded-xl border border-white/5 flex flex-col items-center text-center hover:bg-white/5 transition-colors" data-id="element-174">
                <metric.icon className="text-primary mb-2" size={24} data-id="element-175" />
                <span className="font-bold text-white" data-id="element-176">{metric.label}</span>
                <span className="text-xs text-text-dim" data-id="element-177">{metric.desc}</span>
              </div>)}
          </div>

          <div className="text-center" data-id="element-178">
            <p className="text-xl md:text-2xl font-bold text-white leading-relaxed" data-id="element-179">
              "Know <span className="text-primary" data-id="element-180">EXACTLY</span> where{' '}
              <span className="text-primary" data-id="element-181">EVERY</span> customer found you and
              throw more money in that direction."
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-4 text-sm text-text-muted" data-id="element-182">
              <span className="px-3 py-1 bg-white/5 rounded-full border border-white/10" data-id="element-183">
                Number pools for call tracking
              </span>
              <span className="px-3 py-1 bg-white/5 rounded-full border border-white/10" data-id="element-184">
                UTM parameters for CRM attribution
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>;
}